import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Share2 } from 'lucide-react';

const AboutTheJourney = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/blog"
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-orange-500 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Blog</span>
        </Link>

        {/* Article Header */}
        <article>
          <header className="mb-12">
            <span className="inline-block px-3 py-1 bg-orange-500/20 text-orange-500 text-sm font-semibold rounded-full border border-orange-500/30 mb-4">
              Story
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              About the Journey
            </h1>
            <div className="flex items-center space-x-6 text-gray-400 mb-8">
              <div className="flex items-center space-x-2">
                <User className="w-5 h-5" />
                <span>Tu Dang (Tusdang)</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>January 10, 2025</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                <span>6 min read</span>
              </div>
            </div>
          </header>

          {/* Article Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-8 mb-8">
              <p className="text-2xl text-gray-300 leading-relaxed italic">
                Every product begins with an idea. Twe began with a problem that refused to be ignored.
              </p>
            </div>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">A Journey Shaped by Constraints, Not Comfort</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Twe was founded by <strong className="text-orange-500">Tu Dang (known online as Tusdang)</strong> — a Vietnamese creator, game developer, and young entrepreneur who entered the tech industry long before most of his peers were thinking about careers.
              </p>
              <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-8 mb-6">
                <ul className="space-y-3 text-gray-300">
                  <li><strong className="text-white">At 15</strong>, he received his first interview invitation from a game company.</li>
                  <li><strong className="text-white">At 17</strong>, he was already contributing to large-scale Roblox projects run by established studios.</li>
                  <li><strong className="text-white">By 18</strong>, he had founded <strong className="text-orange-500">TweNa Inc.</strong>, partnered with Superbullet Studio and DoBig, launched games, and built real products with real users.</li>
                </ul>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                But the journey was never linear — or easy.
              </p>
              <p className="text-gray-300 leading-relaxed">
                While others followed conventional academic paths, Tu immersed himself in creation, business systems, and real-world experimentation — areas rarely taught in classrooms. His ambition often collided with expectations, limitations, and resistance, including a lack of resources and support at critical moments.
              </p>
              <p className="text-xl text-orange-500 font-semibold mt-6">
                What remained constant was not comfort — but persistence.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">From Personal Friction to a Universal Solution</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                During years of building under pressure, one pattern became clear:
              </p>
              <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-8 mb-6">
                <p className="text-2xl text-white font-semibold mb-4">
                  The biggest obstacle wasn't lack of intelligence or motivation — it was <span className="text-orange-500">friction</span>.
                </p>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                Too many tools. Too many steps. Too many barriers between an idea and execution.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                This friction affected everyone — founders, developers, creators — and hit even harder for people with physical or accessibility challenges.
              </p>
              <p className="text-xl text-white font-semibold">
                Twe was born from that realization.
              </p>
              <p className="text-lg text-gray-300 mt-4">
                Not as a theoretical AI concept — but as a <strong className="text-orange-500">practical system designed to remove friction</strong> and return control to the user.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Building with Purpose, Not Noise</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Twe was not built to chase trends. It was built to solve a real problem experienced firsthand.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Every design decision reflects that philosophy:
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
                  <p className="text-white font-semibold">simplicity over complexity,</p>
                </div>
                <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
                  <p className="text-white font-semibold">voice over friction,</p>
                </div>
                <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
                  <p className="text-white font-semibold">system-level control over surface-level convenience,</p>
                </div>
                <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
                  <p className="text-white font-semibold">accessibility as a foundation — not an add-on.</p>
                </div>
              </div>
              <p className="text-xl text-orange-500 font-semibold">
                The goal was never to impress — but to empower.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">A Journey Still in Motion</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                The story behind Twe is not a story of overnight success. It is a story of <strong className="text-white">learning early, failing fast, and building with intention</strong>.
              </p>
              <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-8 mb-6">
                <p className="text-lg text-gray-300 leading-relaxed">
                  From game development to AI systems, from limited resources to global ambition, the journey continues — driven by a belief that:
                </p>
                <p className="text-2xl text-orange-500 font-bold mt-4">
                  The right tools don't just increase productivity. They change who gets to participate in the future.
                </p>
              </div>
              <p className="text-xl text-white font-semibold">
                Twe is one step in that journey — and only the beginning.
              </p>
            </section>

            <section>
              <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-white mb-6 text-center">The Philosophy Behind Twe</h2>
                <div className="space-y-4 text-lg text-gray-300">
                  <p>✦ <strong className="text-white">Built by a builder</strong>, not a corporation</p>
                  <p>✦ <strong className="text-white">Shaped by real constraints</strong>, not comfort</p>
                  <p>✦ <strong className="text-white">Driven by purpose</strong>, not trends</p>
                  <p>✦ <strong className="text-white">Designed for empowerment</strong>, not exclusion</p>
                </div>
                <p className="text-2xl text-orange-500 font-bold mt-8 text-center">
                  This is the journey. And it's just beginning.
                </p>
              </div>
            </section>
          </div>

          {/* Share & Navigate */}
          <div className="mt-16 pt-8 border-t border-white/5">
            <div className="flex items-center justify-between">
              <Link
                to="/blog"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-orange-500 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Blog</span>
              </Link>
              <button className="inline-flex items-center space-x-2 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors">
                <Share2 className="w-5 h-5" />
                <span>Share</span>
              </button>
            </div>
          </div>

          {/* Previous Article */}
          <div className="mt-12 bg-zinc-900/50 border border-white/5 rounded-xl p-6">
            <p className="text-sm text-gray-400 mb-3">Previous Article</p>
            <Link
              to="/blog/why-twe-exists"
              className="text-2xl font-bold text-white hover:text-orange-500 transition-colors"
            >
              ← Why Twe Exists
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default AboutTheJourney;